import React from 'react';

import { WidgetsMapComponentKeys } from 'config/widgetConfigurations/widgetsConfigurations';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { ITopOverdueData } from 'api/mutations/extractDelayDriverCsv.mutation';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IRenderCriticalAnalysisDelayDrivers } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { useFinishDateFuturePotentialDelayDriversLogic } from 'components/widgets/FinishDateFuturePotentialDelayDrivers/components/FinishDateFuturePotentialDelayDrivers.logic';
import { useFinishDateFuturePotentialDelayDriversProvider } from 'components/widgets/FinishDateFuturePotentialDelayDrivers/components/FinishDateFuturePotentialDelayDrivers.provider';
import {
    columnsV8,
    initialState,
} from 'components/widgets/FinishDateFuturePotentialDelayDrivers/components/FinishDateFuturePotentialDelayDrivers.utils';
import { CriticalAnalysisDelayDriversFilter } from 'components/widgets/FinishDateFuturePotentialDelayDrivers/components/FinishDateFuturePotentialDelayDriversFilter';

const title = 'Critical Activities & Delay Drivers (Risk Lookahead)';
const id = 'futurePotentialDelayDrivers';
const componentKey: WidgetsMapComponentKeys = 'finishDateFuturePotentialDelayDrivers';

export const FinishDateFuturePotentialDelayDrivers = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    localFilters,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
}: IRenderCriticalAnalysisDelayDrivers) => {
    const { route, subTitle } = useBaseWidget({
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        id,
        widgetId,
    });

    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
    });

    const { ui, api } = useFinishDateFuturePotentialDelayDriversProvider({
        widgetId,
        localFilters,
        projectId,
        contractId,
        globalFilterData,
        latestVersionId,
        table,
    });

    const { logic } = useFinishDateFuturePotentialDelayDriversLogic({
        api,
        ui,
        table,
        title,
        subTitle,
    });

    return (
        <ErrorBoundary>
            <WidgetWithTitle
                id={id}
                title={<WidgetTitle title={title} />}
                titleFilters={[
                    editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                    widgetId && ui.pills.length > 0 ? (
                        <PinboardWidgetPills key={'widgetFilters'} filters={ui.pills} />
                    ) : (
                        <CriticalAnalysisDelayDriversFilter
                            key={'filter'}
                            handleOnSearch={logic.handleOnSearch}
                            dateFilter={ui.state.dateFilter}
                            handleDateFilter={logic.handleDate}
                            months={logic.months}
                            handleStatus={logic.handleStatus}
                            handleStatusType={logic.handleStatusType}
                            statusFilter={ui.state.statusFilter}
                            typeFilter={ui.state.typeFilter}
                            handleCriticalPath={logic.handleCriticalPath}
                            isCriticalPath={ui.state.isCriticalPath}
                            handleDelayDriver={logic.handleDelayDriver}
                            isDelayDriver={ui.state.isDelayDriver}
                        />
                    ),
                ]}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        callBack={table.setWidgetColumns}
                        shouldHide={true}
                    />,
                    <ExportDropdown
                        key={'export'}
                        title={title}
                        handleAPICsvExport={logic.handleAPICsvExport}
                        subTitle={subTitle}
                    />,
                    <ColumnsManager
                        onOpenModal={() => table.setModalColumnsOpen(true)}
                        onHideModal={() => table.setModalColumnsOpen(false)}
                        showModal={table.modalColumnsOpen}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        tableInstance={table.tableInstance}
                        initialState={initialState}
                        columns={columnsV8}
                        onChangeOrder={table.setColumnOrder}
                        onChangeVisibility={table.setColumnVisibility}
                    />,
                    <TooltipIcon key={'info'} tooltip={api.getActivities.data?.info} />,
                    <ThreeDotsMenu
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={title}
                        shareLink={route}
                    />,
                ]}
            >
                <TableContainer>
                    {table.updatedInitialState && (
                        <TableV8<ITopOverdueData>
                            data={logic.filteredRows}
                            columns={columnsV8}
                            isLoading={api.getActivities.isLoading || api.getActivities.isFetching}
                            noData={<NoData>No activities match the applied filter</NoData>}
                            sorting={table.sorting}
                            setSorting={table.setSorting}
                            columnVisibility={table.columnVisibility}
                            setColumnVisibility={table.setColumnVisibility}
                            columnOrder={table.columnOrder}
                            setColumnOrder={table.setColumnOrder}
                            rowSelection={table.rowSelection}
                            setRowSelection={table.setRowSelection}
                            totalPages={table.totalPages}
                            pagination={table.pagination}
                            setPagination={table.setPagination}
                            isPaginationDisabled={api.getActivities.isFetching}
                            displayPaginationBottomBar
                        />
                    )}
                </TableContainer>
            </WidgetWithTitle>
        </ErrorBoundary>
    );
};
